/**
 * Phase 8A — Create / edit an MIS template (name, description, cadence,
 * start FY, actuals source). Sections and sub-cats come in Phase 8B.
 */

import { FunctionComponent, useEffect, useState } from "react";
import {
  ALLOWED_MIS_CADENCE,
  ACTUALS_SOURCE_LABEL,
  CADENCE_LABEL,
  Mis,
  MisActualsSource,
  MisCadenceMonths,
} from "../models/mis_model";

interface Props {
  open: boolean;
  initial?: Mis | null;
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    description: string;
    cadence_months: MisCadenceMonths;
    start_fy: string;
    actuals_source: MisActualsSource;
  }) => Promise<void> | void;
}

const suggestFY = () => {
  const now = new Date();
  const yr = now.getFullYear();
  const short = (y: number) => String(y).slice(-2);
  // April–March FY.
  const startYear = now.getMonth() >= 3 ? yr : yr - 1;
  return `FY${short(startYear)}-${short(startYear + 1)}`;
};

const MisCreateEditModal: FunctionComponent<Props> = ({
  open,
  initial,
  onClose,
  onSubmit,
}) => {
  const isEdit = !!initial;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [cadence, setCadence] = useState<MisCadenceMonths>(1);
  const [startFy, setStartFy] = useState(suggestFY());
  const [actualsSource, setActualsSource] =
    useState<MisActualsSource>("projected");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setName(initial.name);
      setDescription(initial.description || "");
      setCadence(initial.cadence_months);
      setStartFy(initial.start_fy);
      setActualsSource(initial.actuals_source);
    } else {
      setName("");
      setDescription("");
      setCadence(1);
      setStartFy(suggestFY());
      setActualsSource("projected");
    }
    setError(null);
    setSaving(false);
  }, [open, initial]);

  if (!open) return null;

  const handleSubmit = async () => {
    setError(null);
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!startFy.trim()) {
      setError("Start FY is required.");
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim(),
        cadence_months: cadence,
        start_fy: startFy.trim(),
        actuals_source: actualsSource,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800">
            {isEdit ? "Edit MIS" : "Create MIS"}
          </h2>
          <button
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Name<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Standard P&L"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-myPrimaryColor"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional — what this MIS is for"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-myPrimaryColor"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Cadence<span className="text-red-500">*</span>
              </label>
              <select
                value={cadence}
                onChange={(e) =>
                  setCadence(Number(e.target.value) as MisCadenceMonths)
                }
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-myPrimaryColor"
              >
                {ALLOWED_MIS_CADENCE.map((c) => (
                  <option key={c} value={c}>
                    {CADENCE_LABEL[c]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Start FY<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={startFy}
                onChange={(e) => setStartFy(e.target.value)}
                placeholder="FY26-27"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-myPrimaryColor"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Expense Actuals Source<span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              {(["projected", "actual"] as MisActualsSource[]).map((src) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setActualsSource(src)}
                  className={`flex-1 border rounded px-3 py-2 text-xs text-left ${
                    actualsSource === src
                      ? "border-myPrimaryColor bg-myPrimaryColor bg-opacity-10 text-myPrimaryColor font-semibold"
                      : "border-gray-300 text-gray-600 hover:border-gray-400"
                  }`}
                >
                  {ACTUALS_SOURCE_LABEL[src]}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Projected pulls approved project budgets. Actual pulls invoices &
              payouts. Toggleable any time after creation.
            </p>
            {isEdit && initial && actualsSource !== initial.actuals_source && (
              <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                <span className="font-semibold">Heads up:</span> switching the
                actuals source retroactively changes the expense numbers
                shown in this MIS's P&amp;L grid for every past period. Revenue
                entries are unaffected.
              </div>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 text-sm font-semibold text-white bg-myPrimaryColor hover:bg-mySecondaryColor rounded disabled:opacity-50"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create MIS"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MisCreateEditModal;
